<script setup>
	import { computed } from 'vue';
	import { DsIconButton, DsBadge, DsText } from '@coloplast/design-system';

	const props = defineProps({
		item: {
			type: Object,
			default: () => {},
		},
		isWithoutAnImage: {
			type: Boolean,
			default: false,
		},
        isRelatedContentItem: {
            type: Boolean,
            default: false,
        },
		isLearningItem: {
			type: Boolean,
			default: false,
		},
        isProductItem: {
            type: Boolean,
            default: false,
        },
	});

	const formattedBusinessArea = computed(() => {
        const businessAreas = props.item.BusinessAreaCategory || props.item.CategoryTags?.BusinessAreaCategories;

		if (businessAreas?.includes(',')) {
			return businessAreas.split(', ', 2);
		} else {
			return businessAreas;
		}
	});

    const formattedDescription = computed(() => {
        if (props.item.Description?.includes('<h')) {
            let indexStart = props.item.Description.indexOf('<h');
            let indexEnd = props.item.Description.indexOf('</h') + 5;
            let subString = props.item.Description.substring(indexStart, indexEnd);
            return props.item.Description.replace(subString, '');
        } else {
            return props.item.Description;
        }
    });
</script>

<template>
	<a
		:href="item.Url"
	   :target="item.LinkTarget"
		class="c-static-width-card"
		:class="{ 'c-static-width-card--no-image': isWithoutAnImage, 'c-static-width-card--related': isRelatedContentItem }"
	>
		<div
			v-if="!isWithoutAnImage"
			class="c-static-width-card__image-wrapper"
            :class="{ 'c-static-width-card__image-wrapper--related': isRelatedContentItem }"
		>
			<div
				v-if="item.ImageUrl && !item.HasDefaultImageUrl"
				class="c-static-width-card__image"
                :class="{ 'c-static-width-card__image--product': isProductItem }"
			>
				<picture>
					<source
						media="(max-width: 767px)"
						:srcset="`${item.ImageUrl}?im=Resize=(327,180)`"
					/>
					<img
						:src="`${item.ImageUrl}?im=Resize=(408,200)`"
						:alt="item.Headline || item.Heading"
					/>
				</picture>
			</div>
			<div
				v-if="item.ImageUrl && item.HasDefaultImageUrl"
				class="c-static-width-card__image c-static-width-card__image--icon"
			>
				<img
					:src="item.ImageUrl"
					:alt="item.Headline || item.Heading || item.DisplayName"
				/>
			</div>
			<div
				v-if="!item.ImageUrl && item.HasDefaultImageUrl"
				class="c-static-width-card__image c-static-width-card__image--background"
			></div>
            <DsBadge
                v-if="item.NewBadgeText || item.NewLabelText"
                :text="item.NewBadgeText || item.NewLabelText"
                :variant="isProductItem ? 'highlight' : 'neutral'"
                class="c-static-width-card__badge"
            >
            </DsBadge>
		</div>
		<div
			class="c-static-width-card__content-wrapper"
			:class="{ 'c-static-width-card__content-wrapper--no-image': isWithoutAnImage }"
		>
			<p v-if="!isWithoutAnImage" class="c-static-width-card__category">
				<template v-if="formattedBusinessArea && Array.isArray(formattedBusinessArea)">
					<span
						v-for="businessAreaItem in formattedBusinessArea"
						:key="businessAreaItem"
					>
						{{ businessAreaItem }}
					</span>
				</template>
				<span
					v-else-if="formattedBusinessArea"
					v-text="formattedBusinessArea"
				>
				</span>
				<span
					v-if="item.ContentTypeCategory || item.CategoryTags?.ContentTypeCategory"
					v-text="item.ContentTypeCategory || item.CategoryTags.ContentTypeCategory"
				>
				</span>
			</p>
			<DsText
				html-element="h3"
				text-style="medium"
				text-type="heading-lg"
				class="c-static-width-card__heading"
				:class="{ 'c-static-width-card__heading--no-image': isWithoutAnImage }">
				<div v-html="item.Headline || item.Heading || item.DisplayName"></div>
			</DsText>
			<DsText
				html-element="p"
				text-style="regular"
				text-type="body-md"
				class="c-static-width-card__description"
				:class="{
					'c-static-width-card__description--no-image': isWithoutAnImage,
					'c-static-width-card__description--learning': isLearningItem,
				}">
				<div v-html="formattedDescription"></div>
			</DsText>
		</div>
		<div
			class="c-static-width-card__button"
			:class="{ 'c-static-width-card__button--no-image': isWithoutAnImage }"
		>
			<ds-icon-button
				aria-label="Navigate forward"
				icon-name="navigate-forward"
				variant="tertiary"
				size="sm"
			/>
		</div>
	</a>
</template>
